import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFormByCode } from '../../api/aprvForms';
import { getCurrentUser } from '../../api/common';
import { createDoc, addApprovalLines, addDocOpinion, addDocFile, addDocAuth, notifyDocSubmit } from '../../api/aprvDoc';
import { lineTypeMap } from '../../utils/commonUtils';
import SubFormRouter from './subforms/SubFormRouter';
import AprvLineSelect from '../../components/OrgSelect/AprvLineSelect';
import '../../css/AprvDocForm.css';
import FileAttachment from '../../components/FileAttachment/FileAttachment';

function AprvDocForm({ onOpenOpinionModal }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const formCode = queryParams.get('form_code');

  const [formInfo, setFormInfo] = useState(null);
  const [writer, setWriter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 입력 상태 관리
  const [docNo, setDocNo] = useState('');
  const [publicType, setPublicType] = useState('user');
  const [docSave, setDocSave] = useState('1');
  const [title, setTitle] = useState('');
  const [formData, setFormData] = useState({});
  const { editorContent, ...jsonFields } = formData;
  const [draftDate, setDraftDate] = useState(new Date().toISOString().split('T')[0]);

  // 결재선 상태
  const [approvalLines, setApprovalLines] = useState([]);   // 결재자/합의자
  const [approvalReaders, setApprovalReaders] = useState([]); // 열람자


  const [showLinePopup, setShowLinePopup] = useState(false);
  const [files, setFiles] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem('token');
        const userInfo = await getCurrentUser(token);
        setWriter({
          user_id: userInfo.userid,
          user_name: userInfo.name,
          user_position: userInfo.position,
          user_dept_code: userInfo.dept_code,
          user_dept_name: userInfo.dept_name,
        });

        const data = await getFormByCode(formCode, token);
        setFormInfo(data.form);
      } catch (err) {
        console.error('데이터 불러오기 실패:', err);
        setError('결재양식 또는 사용자 정보를 불러올 수 없습니다.');
      } finally {
        setLoading(false);
      }
    };

    if (formCode) {
      fetchData();
    }
  }, [formCode]);

  const handleConfirmLineSelection = (selectedTargets) => {
    const approvers = selectedTargets.filter(line => line.line_type !== "lr");
    setApprovalLines(approvers);

    const readers = selectedTargets.filter(line => line.line_type === "lr");
    setApprovalReaders(readers);

    setShowLinePopup(false);
  };

  const handleSubmit = async (action, opinion) => {
    const token = sessionStorage.getItem('token');

    // 1) 문서 메타데이터 payload (JSON)
    const payload = {
      doc_no: docNo,
      inout_type: "1",
      form_code: formInfo.form_code,
      form_name: formInfo.form_name,
      sub_form_code: formInfo.sub_form_code,
      user_id: writer?.user_id,
      user_name: writer?.user_name,
      user_position: writer?.user_position,
      user_dept_code: writer?.user_dept_code,
      user_dept_name: writer?.user_dept_name,
      req_date: draftDate,
      doc_save: docSave,
      title,
      attach_yn: files.length > 0 ? "y" : "n",
      doc_type: "A",
      public_type: publicType,
      doc_status: action === "draft" ? "draft" : "request",
      html_data: editorContent || "",
      json_data: JSON.stringify({
        [formInfo.sub_form_code]: jsonFields,
      }),
    };

    try {
      // 1) 문서 생성 (JSON)
      const doc = await createDoc(payload, token);

       // 2) 첨부파일 업로드 (파일별 호출)
      if (files.length > 0) {
        const uploadPromises = files
          .filter(f => !f.isDeleted && f.isNew && f.file)
          .map(f => addDocFile(doc.aprv_id, f.file, token, formInfo.form_code));
        await Promise.all(uploadPromises);
      }

      console.log(approvalLines);
      console.log(approvalReaders);
      // 3) 결재선 저장
      if (Array.isArray(approvalLines) && approvalLines.length > 0) {
        const linePayload = approvalLines.map((line, i) => ({
          ...line,
          line_no: i + 1,
          aprv_id: doc.aprv_id,
          current_yn: i === 0 ? "y" : "n",
        }));
        await addApprovalLines(doc.aprv_id, linePayload, token);

        const readerPayload = approvalReaders.map(r => {
          const isDept = r.user_id === r.user_dept_code; //두개의 값이 동일하다면 Dept
          return {
            aprv_id: doc.aprv_id,
            target_type: isDept ? "dept" : "user",  
            target_id: r.user_id,
            target_name: r.user_name,
            target_from: "doc",
            target_auth: "read",
          };
        });
        await addDocAuth(doc.aprv_id, readerPayload, token);
      }

      // 4) 결재 의견 저장
      if (action !== "draft" && opinion.trim()) {
        await addDocOpinion(
          doc.aprv_id,
          {
            opinion,
            user_id: writer?.user_id,
            user_name: writer?.user_name,
            user_position: writer?.user_position,
            user_dept_code: writer?.user_dept_code,
            user_dept_name: writer?.user_dept_name,
          },
          token
        );
      }

      // 5) 결재 상신 시 첫 번째 결재자에게 알림
      if (action !== "draft") {
        await notifyDocSubmit(doc.aprv_id, token, "request");
      }

      alert(action === "draft" ? "임시저장 되었습니다." : "결재 상신이 완료되었습니다.");
      if (action === "draft") {
        navigate("/aprvdoc?status=draft");
      } else {
        navigate("/aprvdoc?status=request");
      }

    } catch (err) {
      console.error("결재 상신 실패:", err);
      alert("결재 상신 중 오류가 발생했습니다.");
    }
  };

  if (loading) return <div className="p-4">⏳ 데이터 불러오는 중...</div>;
  if (error) return <div className="p-4 text-danger">❌ {error}</div>;

return (
  <div className="container mt-4">
    <div className="card shadow">
      <div className="card-header bg-primary text-white">
        <h5 className="mb-0">📑 {formInfo?.form_name}</h5>
      </div>
      <div className="card-body">
        <form >

          {/* ✅ 결재선 지정 영역 */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <label className="form-label mb-0">결재선 지정</label>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowLinePopup(true)}
              >
                결재선 추가
              </button>
            </div>

            <div className="table-responsive mt-2 d-flex justify-content-end">
              <table
                className="table table-bordered text-center align-middle"
                style={{ width: `${(approvalLines.length + 1) * 10}%` }}
              >
                <tbody>
                  <tr className="approval-line-header">
                    <td>기안</td>
                    {approvalLines.map(line => (
                      <td key={line.user_id}>
                        {lineTypeMap[line.line_type]}
                      </td>
                    ))}
                  </tr>

                  {/* 2행: 이름/직책 */}
                  <tr>
                    <td>{writer?.user_name} / {writer?.user_position}</td>
                    {approvalLines.map(line => (
                      <td key={line.user_id}>
                        {line.user_name} / {line.user_position}
                      </td>
                    ))}
                  </tr>

                  {/* 3행: 결재 결과 */}
                  <tr>
                    <td>상신</td>
                    {approvalLines.map(line => (
                      <td key={line.user_id}></td>
                    ))}
                  </tr>

                  {/* 4행: 결재 시간 */}
                  <tr>
                    <td>{draftDate}</td>
                    {approvalLines.map(line => (
                      <td key={line.user_id}></td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

          </div>

          {/* ✅ 문서 기본 정보 */}
          <table className="table table-bordered">
            <tbody>
              {approvalReaders && approvalReaders.length > 0 && (
                <tr className="table-four-cols">
                  <td>열람</td>
                  <td colSpan="3">
                    {approvalReaders.map(r => r.user_name).join(", ")}
                  </td>
                </tr>
              )}

              <tr className="table-four-cols">
                <td>기안자</td>
                <td>{writer?.user_name} / {writer?.user_position}</td>
                <td>기안부서</td>
                <td>{writer?.user_dept_name}</td>
              </tr>
              <tr className="table-four-cols">
                <td>문서 번호</td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={docNo}
                    onChange={(e) => setDocNo(e.target.value)}
                  />
                </td>
                <td>공개 범위</td>
                <td>
                  <select
                    className="form-select"
                    value={publicType}
                    onChange={(e) => setPublicType(e.target.value)}
                  >
                    <option value="user">개인</option>
                    <option value="dept">부서</option>
                    <option value="all">전체</option>
                  </select>
                </td>
              </tr>
              <tr className="table-four-cols">
                <td>기안일</td>
                <td>
                  <input
                    type="date"
                    className="form-control"
                    value={draftDate}
                    onChange={(e) => setDraftDate(e.target.value)}
                  />
                </td>
                <td>보존연한</td>
                <td>
                  <select
                    className="form-select"
                    value={docSave}
                    onChange={(e) => setDocSave(e.target.value)}
                  >
                    <option value="">선택</option>
                    <option value="1">1년</option>
                    <option value="3">3년</option>
                    <option value="5">5년</option>
                    <option value="z">영구</option>
                  </select>
                </td>
              </tr>
              <tr className="table-title">
                <td>제목</td>
                <td colSpan="3">
                  <input
                    type="text"
                    className="form-control"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </td>
              </tr>
            </tbody>
          </table>

          {/* ✅ 서브폼 */}
          <SubFormRouter
            subFormCode={formInfo.sub_form_code}
            formData={formData}
            setFormData={setFormData}
            mode="write"
          />
          {/* 첨부파일 */}
          <div className="mb-3">
            <label className="form-label fw-bold">첨부파일</label>
            <FileAttachment files={files} setFiles={setFiles} mode="edit" />
          </div>

          {/* ✅ 액션 버튼 */}
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={() => handleSubmit("draft", "")}
            >
              임시저장
            </button>

            <button
              type="button"
              className="btn btn-primary me-2"
              onClick={() => onOpenOpinionModal("submit", handleSubmit)}
            >
              결재 상신
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate("/aprvdoc")}
            >
              취소
            </button>
          </div>
        </form>
      </div>
    </div>

    {/* ✅ 결재선 선택 팝업 */}
    {showLinePopup && (
      <AprvLineSelect
        writer={writer}
        onConfirm={handleConfirmLineSelection}
        onCancel={() => setShowLinePopup(false)}
        initialSelected={[...approvalLines, ...approvalReaders]}
      />
    )}

  </div>
);



}

export default AprvDocForm;